import React, { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import { SiteContentProvider } from "./contexts/SiteContentContext";
import CookieBanner from "./components/CookieBanner";
import AnalyticsTracker from "./components/AnalyticsTracker";
import SEO from "./components/SEO";
import LocalBusinessSchema from "./components/LocalBusinessSchema";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Pricing from "./components/Pricing";
import HowItWorks from "./components/HowItWorks";
import About from "./components/About";
import Gallery from "./components/Gallery";
import FaqSection from "./components/Faq";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import AuthModal from "./components/AuthModal";
import BookingModal from "./components/BookingModal";
import Dashboard from "./components/Dashboard";
import BookingSuccess from "./pages/BookingSuccess";
import ThankYouPage from "./pages/ThankYouPage";
import PhotoGalleryPage from "./pages/PhotoGalleryPage";
import TermsPage from "./pages/legal/TermsPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import CookiesPage from "./pages/legal/CookiesPage";
import ComplaintsPage from "./pages/legal/ComplaintsPage";
import DisputesPage from "./pages/legal/DisputesPage";

function HomePage() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  // Optional initial tab when opening the dashboard — used by the package
  // offer notification to land directly on "Výhodné balíčky".
  const [dashboardInitialTab, setDashboardInitialTab] = useState(null);

  const handleBook = useCallback((prefill) => {
    setBookingPrefill(prefill || null);
    setBookingOpen(true);
  }, []);

  const handleOpenDashboard = (tab) => {
    if (!user) setAuthOpen(true);
    else {
      setDashboardInitialTab(typeof tab === "string" ? tab : null);
      setDashboardOpen(true);
    }
  };

  const scrollToServices = () => {
    document.getElementById("cenik")?.scrollIntoView({ behavior: "smooth" });
  };

  // Deep links from emails: /?otevrit=rezervace | /?otevrit=rezervovat | /?otevrit=balicky
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get("otevrit");
    if (!target) return;
    if (target === "rezervace") {
      if (user) {
        setDashboardOpen(true);
        window.history.replaceState({}, "", "/");
      } else {
        setAuthOpen(true);
      }
    } else if (target === "balicky") {
      if (user) {
        setDashboardInitialTab("packages");
        setDashboardOpen(true);
        window.history.replaceState({}, "", "/");
      } else {
        setAuthOpen(true);
      }
    } else if (target === "rezervovat") {
      setBookingOpen(true);
      window.history.replaceState({}, "", "/");
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-apex-bg text-apex-cream">
      <SEO
        title="Detailing a čištění vozů Praha"
        description="Profesionální ruční mytí a detailing vozů přímo u vás doma | Apex Detailing"
        path="/"
      />
      <LocalBusinessSchema />
      <Navbar
        onOpenAuth={() => setAuthOpen(true)}
        onOpenBooking={() => handleBook(null)}
        onOpenDashboard={handleOpenDashboard}
      />
      <main>
        <Hero onBook={() => handleBook({ tier: "detailing", serviceType: "interior" })} onScrollServices={scrollToServices} />
        <Pricing onBook={handleBook} />
        <Gallery />
        <About />
        <HowItWorks />
        <FaqSection />
        <Contact />
      </main>
      <Footer />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} prefill={bookingPrefill} />
      <Dashboard open={dashboardOpen} onClose={() => { setDashboardOpen(false); setDashboardInitialTab(null); }} initialTab={dashboardInitialTab} />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CookieConsentProvider>
          <SiteContentProvider>
            <BrowserRouter>
              <AnalyticsTracker />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/booking-success" element={<BookingSuccess />} />
                <Route path="/dekujeme" element={<ThankYouPage />} />
                <Route path="/fotogalerie" element={<PhotoGalleryPage />} />
                <Route path="/pravni/obchodni-podminky" element={<TermsPage />} />
                <Route path="/pravni/ochrana-osobnich-udaju" element={<PrivacyPage />} />
                <Route path="/pravni/cookies" element={<CookiesPage />} />
                <Route path="/pravni/reklamacni-rad" element={<ComplaintsPage />} />
                <Route path="/pravni/reseni-sporu" element={<DisputesPage />} />
              </Routes>
              <CookieBanner />
            </BrowserRouter>
          </SiteContentProvider>
        </CookieConsentProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
